/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 * 
 */

import { Database } from "../database";
import { ClassificationLabel } from "../classification-label";
import { EntityLabel } from "../entity-label";
import { GroupingLabel } from "../grouping-label";
import { fetchRetry } from "../fetch-retry";

/**
 * Supported review types:
 * - "DocSplitter": Should be used for CDS
 * - "DAI_COMPLEX_CLASSIFICATION": Should be used for CDC
 * - "Entity": Should be used for CDE and specialized parsers like Invoice
 */
export type ReviewRequestType =
  | "DocSplitter"
  | "DAI_COMPLEX_CLASSIFICATION"
  | "Entity";

  /**
   * @enum
   * Enum for review status
   */
  enum ReviewStatus {
    Uploaded = "hochgeladen",
    Processing = "wird verarbeitet",
    Processed = "in Bearbeitung",
    Hitl = "Dokument prüfen",
    Completed = "Dokument fertig verarbeitet",
    Downloaded = "heruntergeladen"
  }

/**
 * Dataclass for review-requests
 * @class ReviewRequest
 */
export class ReviewRequest {
  /**
   * The unique identifier for the review-request
   */
  reviewRequestID: string;
  /**
   * The type of the review-request
   */
  type: ReviewRequestType;
  /**
   * The name of the client
   */
  clientName: string;
  /**
   * The unique identifier for client
   */
  clientId: string;
  /**
   * The name of the consultant
   */
  consultantName: string;
  /**
   * The unique identifier for consultant
   */
  consultantID: string;
  /**
   * The original filename of the document
   */
  originalFilename: string;
  /**
   * The original status of the document in Spanner
   */
  originalStatus: string;
  /**
   * The status of the document converted from english to german
   */
  status: string;
  /**
   * The timestamp when the review request was created
   */
  createTime: string;
  /**
   * The timestamp of the last time the review-request got changed
   */
  lastStatusUpdateTime: string;
  /**
   * The result bucket
   */
  resultBucket: string;
  /**
   * The result object
   */
  resultObject: string;
  /**
   * The callback url for workflows
   */
  callbackUrl: string;
  /**
   * The page image URL prefix
   */
  pageImageUrlPrefix: string;
  /**
   * The page image names
   */
  pageImageNames: Array<string> = [];
  /**
   * The userid of the user which reviewed the document
   */
  reviewedByUserId: string;
  /**
   * The userid of the user which uploaded the document
   */
  uploadedByUserId:string;
  /**
   * The labels
   */
  labels: EntityLabel[] | GroupingLabel[] | ClassificationLabel[];
  /**
   * The splitted PDF objects
   */
  splittedPdfObjects: Array<string>;
  /**
   * The entities after review
   */
  reviewedEntities: Array<any> = [];
  /**
   * The entities before the review
   */
  originalEntities: Array<any> = [];

 /**
   * @constructor
   * @param {string} reviewRequestID - The unique identifier for the review-request
   * @param {ReviewRequestType} type - The type of the review request.
   * @param {string} clientName - The name of the client.
   * @param {string} clientId - The ID of the client.
   * @param {string} consultantName - The name of the consultant.
   * @param {string} consultantID - The ID of the consultant.
   * @param {string} originalFilename - The original filename.
   * @param {string} originalStatus - The original status.
   * @param {string} status - The status.
   * @param {string} createTime - The creation time.
   * @param {string} lastStatusUpdateTime - The last status update time.
   * @param {string} resultBucket - The result bucket.
   * @param {string} resultObject - The result object.
   * @param {string} callbackUrl - The callback URL for workflows.
   * @param {string} pageImageUrlPrefix - The page image URL prefix.
   * @param {Array<string>} [pageImageNames=[]] - The page image names.
   * @param {string} reviewedByUserId - The userid of the user which reviewed the document.
   * @param {string} uploadedByUserId - The userid of the user which uploaded the document.
   * @param {(EntityLabel[] | GroupingLabel[] | ClassificationLabel[])} [labels=[]] - The labels.
   * @param {Array<string>} [splittedPdfObjects=[]] - The splitted PDF objects.
   * @param {Array<any>} [reviewedEntities=[]] - The entities after review.
   * @param {Array<any>} [originalEntities=[]] - The entities before review.
   */
  constructor(
    reviewRequestID: string,
    type: ReviewRequestType,
    clientName: string,
    clientId: string,
    consultantName: string,
    consultantID: string,
    originalFilename: string,
    originalStatus: string,
    status: string,
    resultBucket: string,
    resultObject: string,
    callbackUrl: string,
    createTime: string,
    lastStatusUpdateTime: string,
    pageImageUrlPrefix: string,
    pageImageNames: Array<string> = [],
    reviewedByUserId: string,
    uploadedByUserId:string,
    labels: EntityLabel[] | GroupingLabel[] | ClassificationLabel[] = [],
    splittedPdfObjects: Array<string> = [],
    reviewedEntities: Array<any>,       //Get from hitl (if equal to original, dont load it), in another case load the document to datastore
    originalEntities: Array<any>        
  ) {
    this.reviewRequestID = reviewRequestID;
    this.type = type; 
    this.clientName = clientName;
    this.clientId = clientId;
    this.consultantName = consultantName;
    this.consultantID = consultantID;
    this.originalFilename = originalFilename;
    this.originalStatus = originalStatus;
    this.status = status;
    switch(this.originalStatus){
        case "uploaded":
            this.status = ReviewStatus.Uploaded;
            break;
        case "processing":
            this.status = ReviewStatus.Processing;
            break;
        case "processed":
            this.status = ReviewStatus.Processed;
            break;
        case "hitl":
            this.status = ReviewStatus.Hitl;
            break;
        case "completed":
            this.status = ReviewStatus.Completed;
            break;           
        case "downloaded":
          this.status = ReviewStatus.Downloaded;
          break;   
    }
    this.createTime = createTime;  
    this.lastStatusUpdateTime = lastStatusUpdateTime;
    this.resultBucket = resultBucket; 
    this.resultObject = resultObject;
    this.callbackUrl = callbackUrl; 
    this.pageImageUrlPrefix = pageImageUrlPrefix;
    this.pageImageNames = pageImageNames; 
    this.reviewedByUserId = reviewedByUserId; 
    this.uploadedByUserId = uploadedByUserId; 
    this.labels = labels; 
    this.splittedPdfObjects = splittedPdfObjects;
    this.reviewedEntities = reviewedEntities;
    this.originalEntities = originalEntities;
  }

  /**
   * Lists all review requests from the database
   * @static
   * @async
   * @function list
   * @param {Database} database - Database object
   * @param {string} accessToken - Access token for database access
   * @returns {Promise<ReviewRequest[]>} - Promise resolving to an array of ReviewRequest objects
   */
  static async list(database: Database, accessToken: string): Promise<ReviewRequest[]> {
    let reviewRequests: ReviewRequest[] = [];
    const sql = `SELECT re.reviewRequestID,
      re.processorType,
      cl.name as clientName,
      cl.clientID,
      con.name as consultantName,
      con.consultantID,
      re.originalFilename,
      re.status,
      re.resultBucket,
      re.resultObject,
      re.callbackUrl,
      re.createTime,
      re.lastStatusUpdateTime,
      re.pageImageUrlPrefix,
      re.pageImageNames,
      re.reviewedByUserId,
      re.uploadedByUserId,
      re.labels,
      re.splittedPdfObjects,
      re.reviewedEntities,
      re.originalEntities
    FROM
        ReviewRequest re
    JOIN
        ReviewRequestConsultantClient reccl
    ON
        reccl.reviewRequestID = re.reviewRequestID
    JOIN
        Consultant con
    ON
        reccl.consultantID = con.consultantID
    JOIN
        Client cl
    ON
        reccl.clientID = cl.clientID;`;
    const rows = await database.read(accessToken, sql); 
    rows.forEach((row: any) => {
      let type: ReviewRequestType;
      let labels;
      if (row.processorType === "CUSTOM_SPLITTING_PROCESSOR") {
        type = "DocSplitter";
        labels = JSON.parse(row.labels) as GroupingLabel[];
      }
      else if (row.processorType === "CUSTOM_CLASSIFICATION_PROCESSOR") {
        type = "DAI_COMPLEX_CLASSIFICATION";
        labels = JSON.parse(row.labels) as ClassificationLabel[];
      }
      else {
        type = "Entity";
        labels = JSON.parse(row.labels) as EntityLabel[];
      }
      const reviewRequest = new ReviewRequest(row.reviewRequestID, type, row.clientName, row.clientID, row.consultantName, row.consultantID, row.originalFilename,  row.status, "", row.resultBucket, row.resultObject, row.callbackUrl, row.createTime, row.lastStatusUpdateTime, row.pageImageUrlPrefix, row.pageImageNames, row.reviewedByUserId, row.uploadedByUserId, labels, row.splittedPdfObjects, row.reviewedEntities, row.originalEntities);
      reviewRequests.push(reviewRequest);
    });
    console.log("ReviewRequests", reviewRequests);
    return reviewRequests;
  }

  /**
   * Updates the status of the review request
   * @async   
   * @function updateStatus
   * @param {Database} database - Database object
   * @param {string} accessToken - Access token for database access
   * @param {string} status - New status for the review request
   */
  async updateStatus(database: Database, accessToken: string, status: string) {
    const sql = `UPDATE ReviewRequest SET status = '${status}' WHERE reviewRequestID = '${this.reviewRequestID}'`;
    database.readWrite(accessToken, sql);
    this.originalStatus = status;
  }

  /**
   * Deletes the review request from the database
   * @async
   * @function delete
   * @param {Database} database - Database object
   * @param {string} accessToken - Access token for database access
   */
  async delete(database: Database, accessToken: string) {
    const sql = `DELETE FROM ReviewRequest WHERE reviewRequestID = '${this.reviewRequestID}'`;
    database.readWrite(accessToken, sql);
  }

  /**
   * Deletes multiple review requests from the database
   * @static
   * @async
   * @function deleteReviewRequests   
   * @param {Database} database - Database object
   * @param {string} accessToken - Access token for database access
   * @param {Array<string>} selectedReviewRequestsIDs - Array of review request IDs to delete
   */
  static async deleteReviewRequests(database: Database, accessToken: string, selectedReviewRequestsIDs: Array<string>) {
    const sql = `DELETE FROM ReviewRequest WHERE reviewRequestID IN ('${selectedReviewRequestsIDs.join("','")}')`;
    database.readWrite(accessToken, sql);
  }

  /**
  * Fetches the content of the review request from the specified URL
  * @async
  * @function getContent
  * @param {string} accessToken - Access token for fetching content
  * @returns {Promise<any>} - Promise resolving to the fetched content
  */
  async getContent(accessToken:string) {
    const url = "https://" + this.resultBucket + ".storage.googleapis.com/" + this.resultObject;
    console.log("url: "+url);
    const response = await fetchRetry(url, {
      headers: {
        Authorization: "Bearer " + accessToken,
      },
    });
    if (!response.ok) {
      throw response;
    }
    const json = await response.json();
    return json;
  }

  /**
   * Saves content to the specified URL
   * @async 
   * @function saveContent
   * @param {string} accessToken - Access token for saving content
   * @param {string} content - Content to be saved
   * @returns {Promise<void>} - Promise indicating completion of the save operation
   */
  async saveContent(
    accessToken: string,
    content: string,
    rejectReason: any = null
  ): Promise<void> {
    
    // save content to GCS
    let url;
    url = `https://storage.googleapis.com/${this.resultBucket}/${this.resultObject}`;

    await fetchRetry(url, {
      method: "PUT",
      headers: {
        Authorization: "Bearer " + accessToken,
        "Content-Type": "application/json",
      },
      body: content,
    });

    // trigger Workflow callback
    url = this.callbackUrl;

    await fetchRetry(url, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + accessToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        reviewedByUserId: this.reviewedByUserId
      })
    });
  }
}